import React from "react";
import { Link } from "react-router-dom";
import s from "../../style/about.module.less";
import { TOOL_COUNT } from "../../lib/toolMeta";
import { TOOL_PATHS } from "../../lib/slugs";
import { useDocumentMeta } from "../../hooks/useDocumentMeta";
import { APP_ORIGIN } from "../../lib/appBase";
import { DappStoreBadge } from "../common/DappStoreBadge";

export const ABOUT_TITLE = "About Solstack";
export const ABOUT_DESCRIPTION = `Solstack is a Solana token toolkit: ${TOOL_COUNT} tools to create, launch, send, lock and inspect tokens from your own wallet, with every fee shown before you sign.`;

const landing = (id) => `/tools/${TOOL_PATHS[id]}`;

/** /about — what Solstack is, how it's run, and how to reach it. Every
 * statement here is one the rest of the site already makes; it exists so
 * search engines and readers have one page that says who is behind the
 * tools and how they work. Prerendered like the other marketing routes
 * (src/prerender/entry.js). */
export const About = () => {
  useDocumentMeta({ title: ABOUT_TITLE, description: ABOUT_DESCRIPTION, path: "/about", siteName: "Solstack" });

  return (
    <article className={s.wrap}>
      <span className={s.eyebrow}>About</span>
      <h1 className={s.h1}>About Solstack</h1>
      <p className={s.lede}>
        Solstack is a set of {TOOL_COUNT} Solana token tools in one place: create a token, launch it on a bonding
        curve or open a pool for it, send it to a list or to every holder of another token, manage its authorities,
        lock what you promise and inspect any mint before you buy. Each tool runs from your own wallet, and each fee is shown before you
        sign.
      </p>

      <div className={s.facts}>
        <div className={s.fact}>
          <b>{TOOL_COUNT}</b>tools, one wallet connection
        </div>
        <div className={s.fact}>
          <b>Sep 2026</b>public launch
        </div>
        <div className={s.fact}>
          <b>1 signature</b>for a multisend of any size
        </div>
        <div className={s.fact}>
          <b>0 SOL</b>for every read-only tool
        </div>
      </div>

      <h2 className={s.h2}>How the tools work</h2>
      <p className={s.p}>
        Most tools run entirely in your browser: Solstack builds a transaction, your wallet signs it, and nothing
        here sees or stores a private key. Four tools go one step further. The{" "}
        <Link to={landing("multisender")}>Multisender</Link> and the <Link to={landing("airdrop")}>Airdrop</Link>{" "}
        take a single approval from your wallet and finish the send on Solstack's server, using a key made for
        that one job that can only move what the job needs. The{" "}
        <Link to={landing("grid-bot")}>Grid Trading Bot</Link> and the{" "}
        <Link to={landing("liquidity-bot")}>DLMM Liquidity Bot</Link> trade from a wallet of their own, which you
        fund once and the server holds the key to until you stop the bot. The{" "}
        <Link to="/docs/security">security page</Link> describes exactly what those keys can and can't do.
      </p>
      <p className={s.p}>
        Read-only tools, the <Link to={landing("token-inspector")}>Token Inspector</Link>, the{" "}
        <Link to={landing("account")}>Account Inspector</Link>, the lock verifiers and the rest, are free and need no
        wallet. Tools that create or distribute something charge a flat fee; the bots add 0.5% of each swap they
        make, and Wallet Cleaner keeps 2% of the rent and of sales. Rent Reclaim is free. Every fee is listed on each
        tool's page and itemised on the review screen before you sign; the network's own rent and fees are shown
        beside it.
      </p>

      <h2 className={s.h2}>What's here</h2>
      <ul className={s.list}>
        <li>
          <Link to="/tools">The tools</Link>: a page for each one, with what it does, how to use it and what it
          costs.
        </li>
        <li>
          <Link to="/docs/overview">The docs</Link>: a full guide to every tool, plus the concepts behind them,
          from token authorities to Token-2022 extensions.
        </li>
        <li>
          <Link to="/academy">The Academy</Link>: lessons on how Solana tokens, accounts and transactions
          actually work, free and in order.
        </li>
        <li>
          <Link to="/blog">The blog</Link>: guides and cost breakdowns, checked against mainnet.
        </li>
        <li>
          <a href="https://www.youtube.com/@Solstack" target="_blank" rel="noopener noreferrer">
            YouTube
          </a>
          : each tool walked through on screen.
        </li>
      </ul>

      <h2 id="solana-mobile" className={s.h2}>
        On your phone
      </h2>
      <p className={s.p}>
        Solstack is an app in the Solana dApp Store, the app store on Solana Mobile's Seeker and Saga phones. Open
        the dApp Store and search for Solstack, or tap the badge below on the phone. The app has the same tools as
        the website and connects to the wallets on the phone, Seed Vault included, through Mobile Wallet Adapter.
      </p>
      <p className={s.p}>
        On any other phone or computer there's nothing to install: open{" "}
        <a href={APP_ORIGIN} rel="nofollow">
          app.solstack.xyz
        </a>{" "}
        in your browser and connect the wallet you already use.
      </p>
      <DappStoreBadge className={s.badge} height={48} />

      <h2 className={s.h2}>Timeline</h2>
      <ul className={s.list}>
        <li>September 2026: the site, the docs and the first tools open to the public.</li>
        <li>
          Since then: launchers for pump.fun, letsbonk.fun, Meteora and StonkFun, the Token Inspector's safety
          score, token and LP lockers with public verifiers, and the Academy.
        </li>
        <li>
          October 2026: adding and removing liquidity on Raydium, Meteora, Orca and PumpSwap, new Raydium CPMM and
          Meteora DAMM v2 pools, a grid trading bot and a DLMM liquidity bot, and Rent Reclaim and Wallet Cleaner
          selling or burning the tokens you no longer want.
        </li>
        <li>October 2026: the Solstack app in the Solana dApp Store, for Seeker and Saga phones.</li>
      </ul>

      <h2 className={s.h2}>Contact</h2>
      <p className={s.p}>
        Support and questions: <a href="mailto:support@solstack.xyz">support@solstack.xyz</a>. Solstack is also on{" "}
        <a href="https://x.com/solstack_xyz" target="_blank" rel="noopener noreferrer">
          X
        </a>
        ,{" "}
        <a href="https://t.me/solstack_xyz" target="_blank" rel="noopener noreferrer">
          Telegram
        </a>
        ,{" "}
        <a href="https://medium.com/@solstackxyz" target="_blank" rel="noopener noreferrer">
          Medium
        </a>{" "}
        and{" "}
        <a href="https://github.com/solstackxyz" target="_blank" rel="noopener noreferrer">
          GitHub
        </a>
        . Always check that you're on solstack.xyz or app.solstack.xyz before connecting a wallet.
      </p>
    </article>
  );
};
